// src/pages/Horarios.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getEmployees, getPosts, getSchedulesRange, MIN_BISEMANA, saveSchedule, saveSchedules, deleteSchedule, getAppointments } from '../data/store';

const DIAS = ['Dom','Lun','Mar','Mie','Jue','Vie','Sab'];
const DIAS_FULL = ['Domingo','Lunes','Martes','Miercoles','Jueves','Viernes','Sabado'];

function getBisemana(offset=0) {
  const base = new Date(2026, 3, 27);
  const today = new Date();
  const diffDays = Math.floor((today - base) / (1000 * 60 * 60 * 24));
  const currentPeriod = Math.floor(diffDays / 14);
  const targetPeriod = currentPeriod + offset;
  const monday = new Date(base);
  monday.setDate(base.getDate() + targetPeriod * 14);
  return Array.from({length:14},(_,i)=>{ const d=new Date(monday); d.setDate(monday.getDate()+i); return d; });
}

function fmt(d){
  return String(d.getMonth()+1).padStart(2,'0') + '/' + String(d.getDate()).padStart(2,'0') + '/' + d.getFullYear();
}

function toISO(d) {
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
}

function normalizeToISO(date) {
  if (!date) return '';
  if (date instanceof Date) return toISO(date);
  if (typeof date === 'string') {
    const s = date.trim();
    if (/^\d{4}-/.test(s)) return s.slice(0, 10);
    if (s.includes('/')) {
      const parts = s.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        const a = parseInt(parts[0], 10);
        const b = parseInt(parts[1], 10);
        const y = parts[2];
        if (b > 12) return y + '-' + String(a).padStart(2,'0') + '-' + String(b).padStart(2,'0');
        else return y + '-' + String(b).padStart(2,'0') + '-' + String(a).padStart(2,'0');
      }
    }
  }
  return '';
}

function parseDateStr(isoStr) {
  if (!isoStr) return new Date();
  const iso = normalizeToISO(isoStr);
  if (!iso) return new Date();
  const [y, m, d] = iso.split('-');
  return new Date(Number(y), Number(m)-1, Number(d));
}

// SIEMPRE muestra minutos con dos dígitos: "10:00 PM - 6:00 AM"
function formatShift12h(shift) {
  if (!shift) return '';
  if (shift.includes('AM') || shift.includes('PM')) return shift.replace('/', ' - ');
  let start = '', end = '';
  if (shift.includes('/')) { [start, end] = shift.split('/'); }
  else if (shift.includes('-')) { [start, end] = shift.split('-'); }
  else return shift;
  const convert = (time) => {
    if (!time) return '';
    let [h, m] = time.split(':');
    h = parseInt(h, 10);
    m = m ? String(m).padStart(2,'0') : '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + m + ' ' + ampm;
  };
  return convert(start) + ' - ' + convert(end);
}

function to24h(hour, min, ampm) {
  if (!hour) return '';
  let h = parseInt(hour, 10);
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return String(h).padStart(2,'0') + ':' + (min || '00');
}

function checkConflict(emp, date, shift) {
  if(!emp) return null;
  const iso = normalizeToISO(date);
  if (!iso) return null;
  const [y,mo,d] = iso.split('-');
  const dt = new Date(Number(y), Number(mo)-1, Number(d));
  const dow = dt.getDay();
  const r = emp.preferred_shifts || emp.restrictions || [];
  const getStartHour = (s) => { if (!s) return 0; const st = s.split('/')[0]; if (st.includes(':')) return parseInt(st.split(':')[0],10); return 0; };
  const startHour = getStartHour(shift);
  const isNight = startHour >= 18 || (shift && shift.toLowerCase().includes('noche'));
  const isDay = !isNight;
  if(r.includes('only_night') && isDay) return { type:'block', msg: emp.name + ' SOLO trabaja turno nocturno' };
  if(r.includes('only_day') && isNight) return { type:'block', msg: emp.name + ' SOLO trabaja turno diurno' };
  if(r.includes('no_weekend') && (dow===0||dow===6)) return { type:'warn', msg: emp.name + ' normalmente no trabaja fines de semana' };
  if(r.includes('no_monday') && dow===1) return { type:'warn', msg: emp.name + ' normalmente no trabaja lunes' };
  if(r.includes('no_tuesday') && dow===2) return { type:'warn', msg: emp.name + ' normalmente no trabaja martes' };
  if(r.includes('no_wednesday') && dow===3) return { type:'warn', msg: emp.name + ' normalmente no trabaja miercoles' };
  if(r.includes('no_thursday') && dow===4) return { type:'warn', msg: emp.name + ' normalmente no trabaja jueves' };
  if(r.includes('no_friday') && dow===5) return { type:'warn', msg: emp.name + ' normalmente no trabaja viernes' };
  return null;
}

function shiftToMinutes(shift) {
  if (!shift) return null;
  const parts = shift.includes('/') ? shift.split('/') : shift.split('-');
  if (parts.length !== 2) return null;
  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
  let startMin = toMin(parts[0]); let endMin = toMin(parts[1]);
  if (endMin <= startMin) endMin += 24 * 60;
  return { startMin, endMin };
}
function hasShiftConflict(a,b){const x=shiftToMinutes(a);const y=shiftToMinutes(b);if(!x||!y)return false;return!(x.endMin<=y.startMin||y.endMin<=x.startMin);}
function isBackToBack(a,b){const x=shiftToMinutes(a);const y=shiftToMinutes(b);if(!x||!y)return false;return x.endMin===y.startMin||y.endMin===x.startMin;}

export default function Horarios() {
  const { user, can, addApprovalRequest } = useAuth();
  const [specialShift, setSpecialShift] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [posts, setPosts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [bisemana, setBisemana] = useState(0);
  const [selectedPost, setSelectedPost] = useState('all');
  const [dragEmp, setDragEmp] = useState(null);
  const [modal, setModal] = useState(null);
  const [noPuedeCubrir, setNoPuedeCubrir] = useState(null);
  const [conflictInfo, setConflictInfo] = useState(null);
  const [pendingAssign, setPendingAssign] = useState(null);
  const [approvalModal, setApprovalModal] = useState(null);

  const canEdit = can ? can('canEditSchedules') : true;
  const canDelete = can ? can('canDeleteSchedules') : true;
  const days = getBisemana(bisemana);
  // Rango de turnos que necesita la pantalla: la bisemana visible + la anterior
  // (la anterior la usa "Auto-asignar borrador" para copiar turnos).
  const rangoBisemana = (offset) => {
    const d = getBisemana(offset);
    const desde = new Date(d[0]);
    desde.setDate(desde.getDate() - 14);
    return [desde, d[13]];
  };
  const reload = async () => {
    const [desde, hasta] = rangoBisemana(bisemana);
    const data = await getSchedulesRange(desde, hasta);
    setSchedules(data);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setEmployees(await getEmployees());
        setPosts(await getPosts());
        setAppointments(await getAppointments());
      } catch (error) { console.error('Error cargando horarios:', error); }
    };
    loadData();
  }, []);

  // Turnos: solo se piden los de la bisemana que se esta viendo (se vuelve a pedir al cambiar de bisemana)
  useEffect(() => {
    let cancelado = false;
    const [desde, hasta] = rangoBisemana(bisemana);
    getSchedulesRange(desde, hasta)
      .then((data) => { if (!cancelado) setSchedules(data); })
      .catch((error) => console.error('Error cargando turnos:', error));
    return () => { cancelado = true; };
  }, [bisemana]);

  const schedMap = {};
  schedules.forEach(s => {
    const empId = s.employee_id || s.employeeId;
    const isoKey = normalizeToISO(s.date);
    if (!isoKey) return;
    const key = empId + '_' + isoKey;
    if (!schedMap[key]) schedMap[key] = [];
    schedMap[key].push(s);
  });

  const getEmpSchedule = (empId, date) => {
    const isoKey = normalizeToISO(date);
    if (!isoKey) return [];
    return schedMap[empId + '_' + isoKey] || [];
  };

  // Devuelve info de cita/vacaciones del empleado en esa fecha
  const getEmpAppointment = (empId, date) => {
  const isoDate = normalizeToISO(date);
  return appointments.find(a => {
    const aId = String(a.employee_id || a.employeeId);
    if (aId !== String(empId)) return false;
    const aStart = normalizeToISO(a.date);
    const aEnd = normalizeToISO(a.dateEnd || a.date_end || a.date);
    if (!aStart) return false;
    const endISO = aEnd || aStart;
    return isoDate >= aStart && isoDate <= endISO;
  }) || null;
};

  const handleRestrictedAction = (
  actionLabel,
  empId,
  date,
  shift,
  postId,
  scheduleId = null
) => {
  const emp = employees.find(
    e => String(e.id) === String(empId)
  );

  setApprovalModal({
    actionLabel,
    empId,
    empName: emp ? emp.name : String(empId),
    date: date instanceof Date ? fmt(date) : date,
    dateISO: normalizeToISO(date),
    shift,
    postId,
    scheduleId
  });
};

  // Solicitudes en localStorage para que Admin las vea en Configuracion
  const submitApprovalRequest = (notes) => {
  if (!approvalModal) return;

  if (addApprovalRequest) {

    addApprovalRequest({
      type: 'edit_schedule',

      requestedBy:
        user
          ? user.name
          : 'Desconocido',

      employeeName:
        approvalModal.empName,

      date:
        approvalModal.date,

      shift:
        approvalModal.shift,

      requestedShift:
        document.getElementById(
          'requested_shift'
        )?.value,

      scheduleId:
        approvalModal.scheduleId,

      employeeId:
        approvalModal.empId,

      postId:
        approvalModal.postId,

      dateISO:
        approvalModal.dateISO,

      action:
        approvalModal.actionLabel,

      notes
    });

  }

  setApprovalModal(null);

  alert(
    'Solicitud enviada. Un administrador deberá aprobarla.'
  );
};

  const assignShift = async (empId, date, shift, postId, force=false) => {
    const emp = employees.find(e=>e.id===empId);
    // Bloquear si tiene cita ese dia
    const appt = getEmpAppointment(empId, date);
    if (appt && !force) {
      const tipo = appt.type || 'Cita';
      const ok = window.confirm(emp ? emp.name : empId + ' tiene ' + tipo + ' ese dia. ¿Deseas asignar el turno de todas formas?');
      if (!ok) return;
    }
    if(!force && emp) {
      const conflict = checkConflict(emp, date, shift);
      if(conflict) { setConflictInfo(conflict); setPendingAssign({empId,date,shift,postId}); return; }
    }
    const isoDate = normalizeToISO(date);
    const existingSameDay = schedules.filter(s => String(s.employee_id||s.employeeId)===String(empId) && normalizeToISO(s.date)===isoDate);
    if (existingSameDay.length > 0 && !force) {
      const confirmAdd = window.confirm('Este empleado ya tiene un turno ese dia. Deseas anadir otro?');
      if (!confirmAdd) return;
    }
    await saveSchedule({ id: Date.now().toString(), employee_id: empId, date: isoDate, shift, post_id: postId||null, status:'confirmed' });
    await reload();
    setConflictInfo(null); setPendingAssign(null); setModal(null);
  };

  const openNoPuedeCubrir = (schedule) => {
    setNoPuedeCubrir({ schedule, vacancy: { post: { id:schedule.post_id||schedule.postId, name:schedule.post_name||posts.find(p=>String(p.id)===String(schedule.post_id||schedule.postId))?.name }, date:schedule.date, shift:schedule.shift } });
  };

  const getReplacementOptions = (vacancy) => {
    const available = [], limited = [];
    const vacISO = normalizeToISO(vacancy.date);
    employees.forEach(emp => {
      const empSchedules = schedules.filter(s=>String(s.employee_id||s.employeeId)===String(emp.id)&&normalizeToISO(s.date)===vacISO);
      let conflictReason = '';
      for (const s of empSchedules) {
        if (hasShiftConflict(s.shift,vacancy.shift)) { conflictReason='Trabaja '+formatShift12h(s.shift)+' en otro puesto'; break; }
        if (isBackToBack(s.shift,vacancy.shift)) { conflictReason='Turno pegado'; break; }
      }
      if (conflictReason) limited.push({emp,reason:conflictReason}); else available.push(emp);
    });
    return { available, limited };
  };

  const generateDraftBisemana = async () => {
    const drafts = [];
    days.forEach(day => {
      const currentISO = toISO(day);
      const previousDay = new Date(day); previousDay.setDate(day.getDate()-14);
      const previousISO = toISO(previousDay);
     const previousSchedules = schedules.filter(s =>
  normalizeToISO(s.date) === previousISO &&
  s.shift &&
  s.status !== 'draft'
);
      previousSchedules.forEach(prev => {
        const alreadyExists = schedules.some(s=>normalizeToISO(s.date)===currentISO&&String(s.employee_id||s.employeeId)===String(prev.employee_id||prev.employeeId)&&String(s.post_id||s.postId)===String(prev.post_id||prev.postId)&&s.shift===prev.shift);
        if (alreadyExists) return;
        drafts.push({ id:Date.now().toString()+'_'+Math.random().toString(36).substr(2,9), employee_id:prev.employee_id||prev.employeeId, employee_name:prev.employee_name||prev.employeeName||null, post_id:prev.post_id||prev.postId, post_name:prev.post_name||prev.postName||null, date:currentISO, shift:prev.shift, status:'draft' });
      });
    });
    if (drafts.length > 0) { await saveSchedules(drafts); await reload(); alert('Se crearon '+drafts.length+' borradores.'); }
    else { alert('No se crearon borradores.'); }
  };

  const onDrop = (empId, date) => {
    if(!dragEmp||dragEmp===empId) return;
    getEmpSchedule(dragEmp,date).forEach(src=>assignShift(empId,date,src.shift,src.post_id||src.postId));
    setDragEmp(null);
  };

  const openAssign = (empId, date) => {
    if (!canEdit) { handleRestrictedAction('Asignar turno',empId,date,'',null); return; }
    const post = selectedPost==='all' ? posts[0]||{} : posts.find(p=>String(p.id)===String(selectedPost))||posts[0]||{};
    setSpecialShift(false);
    setModal({ empId, date, postId:post?.id||'', shift:post?.shifts?.[0]||'08:00/16:00', startHour:'8', startMin:'00', startAmpm:'AM', endHour:'4', endMin:'00', endAmpm:'PM' });
  };

  const filteredEmployees = selectedPost==='all' ? employees : employees.filter(emp=>schedules.some(s=>String(s.employee_id||s.employeeId)===String(emp.id)&&String(s.post_id||s.postId)===String(selectedPost)));

  return (
    <div style={{color:'#fff'}}>
      {approvalModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'#1a1a1a',border:'1px solid rgba(245,197,24,0.4)',borderRadius:16,padding:'2rem',width:'100%',maxWidth:440}}>
            <div style={{fontSize:28,textAlign:'center',marginBottom:12}}>🔒</div>
            <h3 style={{color:'#F5C518',margin:'0 0 8px',textAlign:'center'}}>Permiso requerido</h3>
            <p style={{color:'#888',fontSize:13,textAlign:'center',marginBottom:16}}>Tu rol no puede: <strong style={{color:'#fff'}}>{approvalModal.actionLabel}</strong>. Se enviara solicitud al admin.</p>
            {approvalModal.empName&&<p style={{color:'#aaa',fontSize:13,margin:'0 0 4px'}}>Empleado: <strong>{approvalModal.empName}</strong></p>}
            {approvalModal.date&&<p style={{color:'#aaa',fontSize:13,margin:'0 0 4px'}}>Fecha: <strong>{approvalModal.date}</strong></p>}
            <p
  style={{
    color:'#aaa',
    fontSize:13,
    margin:'0 0 8px'
  }}
>
  Turno actual:
  <strong>
    {' '}
    {formatShift12h(approvalModal.shift)}
  </strong>
</p>

<label
  style={{
    display:'block',
    color:'#888',
    fontSize:12,
    marginBottom:4
  }}
>
  Nuevo horario solicitado
</label>

<select
  id="requested_shift"
  defaultValue={approvalModal.shift}
  style={{
    width:'100%',
    padding:'10px',
    background:'#0d0d0d',
    border:'1px solid #333',
    borderRadius:8,
    color:'#fff',
    marginBottom:12,
    boxSizing:'border-box'
  }}
>
  {(posts.find(p => String(p.id) === String(approvalModal.postId))?.shifts || [approvalModal.shift]).map(shift => (
    <option key={shift} value={shift}>
      {formatShift12h(shift)}
    </option>
  ))}
</select>
            <textarea id='approval_notes' placeholder='Notas (opcional)...' style={{width:'100%',padding:'10px',background:'#0d0d0d',border:'1px solid #333',borderRadius:8,color:'#fff',fontSize:13,minHeight:80,boxSizing:'border-box',marginBottom:16,resize:'vertical'}}/>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>submitApprovalRequest(document.getElementById('approval_notes').value)} style={{flex:1,padding:'10px',background:'#F5C518',color:'#000',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer'}}>Enviar solicitud</button>
              <button onClick={()=>setApprovalModal(null)} style={{flex:1,padding:'10px',background:'rgba(220,38,38,0.15)',color:'#f87171',border:'1px solid rgba(220,38,38,0.3)',borderRadius:8,cursor:'pointer',fontWeight:600}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{fontSize:'1.6rem',fontWeight:800,color:'#F5C518',margin:0}}>Horarios</h1>
          <p style={{color:'#666',margin:'4px 0 0',fontSize:14}}>{fmt(days[0])} - {fmt(days[13])}</p>
          {!canEdit&&<p style={{color:'#fbbf24',fontSize:12,margin:'4px 0 0'}}>Solo lectura</p>}
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <button onClick={()=>setBisemana(b=>Math.max(MIN_BISEMANA,b-1))} disabled={bisemana<=MIN_BISEMANA} title={bisemana<=MIN_BISEMANA?'Solo se muestran los ultimos 3 meses':''} style={{background:'#1a1a1a',border:'1px solid #333',color:'#fff',borderRadius:8,padding:'8px 16px',cursor:bisemana<=MIN_BISEMANA?'not-allowed':'pointer',opacity:bisemana<=MIN_BISEMANA?0.4:1}}>Anterior</button>
          <button onClick={()=>setBisemana(0)} style={{background:'rgba(245,197,24,0.15)',border:'1px solid rgba(245,197,24,0.3)',color:'#F5C518',borderRadius:8,padding:'8px 16px',cursor:'pointer'}}>Bisemana actual</button>
          <button onClick={()=>setBisemana(b=>b+1)} style={{background:'#1a1a1a',border:'1px solid #333',color:'#fff',borderRadius:8,padding:'8px 16px',cursor:'pointer'}}>Siguiente</button>
          {canEdit&&(<button onClick={generateDraftBisemana} style={{background:'#fbbf24',border:'none',color:'#000',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontWeight:700}}>Auto-asignar borrador</button>)}
        </div>
      </div>
      <div style={{marginBottom:16}}>
        <select value={selectedPost} onChange={e=>setSelectedPost(e.target.value)} style={{padding:'8px 14px',background:'#1a1a1a',border:'1px solid #333',borderRadius:8,color:'#fff',fontSize:14}}>
          <option value='all'>Todos los puestos</option>
          {posts.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div style={{display:'flex',gap:16,marginBottom:16,fontSize:12,flexWrap:'wrap'}}>
        <span style={{color:'#34d399'}}>● Turno asignado</span>
        <span style={{color:'#fbbf24'}}>● Borrador pendiente</span>
        <span style={{color:'#666'}}>○ Sin turno (click para asignar)</span>
        <span style={{color:'#60a5fa'}}>■ Cita / Vacaciones</span>
        {!canEdit&&<span style={{color:'#fbbf24'}}>🔒 Solo lectura</span>}
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',minWidth:900,width:'100%'}}>
          <thead>
            <tr>
              <th style={{textAlign:'left',padding:'8px 12px',color:'#666',fontSize:12,borderBottom:'1px solid #222',minWidth:150}}>Empleado</th>
              {days.map((d,i)=>{const isToday=toISO(d)===toISO(new Date());return(
                <th key={i} style={{padding:'6px 4px',color:isToday?'#F5C518':'#666',fontSize:11,borderBottom:'1px solid #222',textAlign:'center',minWidth:60,borderLeft:i===7?'2px solid #333':'none'}}>
                  <div style={{fontWeight:600}}>{DIAS[d.getDay()]}</div>
                  <div style={{fontSize:10}}>{d.getMonth()+1}/{d.getDate()}</div>
                </th>
              );})}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map(emp=>(
              <tr key={emp.id} style={{borderBottom:'1px solid #1a1a1a'}} draggable onDragStart={()=>setDragEmp(emp.id)}>
                <td style={{padding:'8px 12px',fontSize:13,whiteSpace:'nowrap'}}>
                  <div style={{fontWeight:500}}>{emp.name}</div>
                  <div style={{color:'#555',fontSize:11}}>{emp.type==='parttime'?'Part-time':'Full-time'}</div>
                </td>
                {days.map((d,i)=>{
                  const appt = getEmpAppointment(emp.id, d);
                  const daySchedules=getEmpSchedule(emp.id,d).filter(s=>s.status!=='draft'&&(selectedPost==='all'||String(s.post_id||s.postId)===String(selectedPost)));
                  const dayDrafts=getEmpSchedule(emp.id,d).filter(s=>s.status==='draft'&&(selectedPost==='all'||String(s.post_id||s.postId)===String(selectedPost)));
                  const isToday=toISO(d)===toISO(new Date());
                  // Color de fondo segun cita/vacaciones
                  const apptBg = appt ? (appt.type==='Vacaciones' ? 'rgba(99,102,241,0.18)' : 'rgba(96,165,250,0.18)') : 'transparent';
                  const cellBg = appt ? apptBg : (isToday?'rgba(245,197,24,0.03)':'transparent');
                  return(
                    <td key={i} style={{padding:'3px',textAlign:'center',background:cellBg,borderLeft:i===7?'2px solid #333':'none',position:'relative'}} onDragOver={e=>e.preventDefault()} onDrop={()=>onDrop(emp.id,d)}>
                      {appt && daySchedules.length===0 && dayDrafts.length===0 && (
                        <div style={{fontSize:9,color:appt.type==='Vacaciones'?'#a5b4fc':'#93c5fd',padding:'4px 2px',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                          {appt.type==='Vacaciones'?'🏖 Vac.':appt.type==='Medica'?'🏥 Cita':'📋 '+appt.type}
                        </div>
                      )}
                      {daySchedules.length>0||dayDrafts.length>0?(
                        <div style={{display:'flex',flexDirection:'column',gap:4}}>
                          {appt && <div style={{fontSize:8,color:'#fbbf24',fontWeight:700}}>{appt.type}</div>}
                          {dayDrafts.map((s)=>(
                            <div key={s.id} onDoubleClick={async()=>{if(!canEdit){handleRestrictedAction('Confirmar borrador',emp.id,d,s.shift,s.post_id||s.postId);return;}await deleteSchedule(s.id);await saveSchedule({...s,id:Date.now().toString(),employee_id:s.employee_id||s.employeeId,post_id:s.post_id||s.postId,date:normalizeToISO(s.date),status:'confirmed'});await reload();}} style={{background:'rgba(251,191,36,0.18)',border:'1px solid rgba(251,191,36,0.5)',borderRadius:4,padding:'3px 2px',cursor:'pointer',fontSize:10,color:'#fbbf24',position:'relative'}}>
                              <div style={{fontWeight:700,fontSize:9}}>{formatShift12h(s.shift)}</div>
                              <div style={{fontSize:8}}>{s.post_name||posts.find(p=>String(p.id)===String(s.post_id||s.postId))?.name||''}</div>
                              <div style={{fontSize:8,color:'#fde68a'}}>Doble click para confirmar</div>
                              {canDelete&&(<button onClick={e=>{e.stopPropagation();deleteSchedule(s.id).then(reload);}} style={{position:'absolute',top:-4,right:-4,background:'#ef4444',border:'none',color:'#fff',borderRadius:'50%',width:14,height:14,fontSize:9,cursor:'pointer'}}>X</button>)}
                            </div>
                          ))}
                          {daySchedules.map((s)=>(
                            <div key={s.id} style={{background:'rgba(52,211,153,0.15)',border:'1px solid rgba(52,211,153,0.3)',borderRadius:4,padding:'3px 2px',cursor:canEdit?'pointer':'default',fontSize:10,color:'#34d399',position:'relative'}} onClick={()=>{if(canEdit)openAssign(emp.id,d);else handleRestrictedAction(
  'Editar turno',
  emp.id,
  d,
  s.shift,
  s.post_id || s.postId,
  s.id
);}}>
                              <div style={{fontWeight:600,fontSize:9}}>{formatShift12h(s.shift)}</div>
                              <div style={{color:'#6ee7b7',fontSize:8}}>{posts.find(p=>String(p.id)===String(s.post_id||s.postId))?.name||''}</div>
                              {canDelete?(<button onClick={e=>{e.stopPropagation();deleteSchedule(s.id).then(reload);}} style={{position:'absolute',top:-4,right:-4,background:'#ef4444',border:'none',color:'#fff',borderRadius:'50%',width:14,height:14,fontSize:9,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>X</button>):(<button
  onClick={e=>{
    e.stopPropagation();
    handleRestrictedAction(
      'Eliminar turno',
      emp.id,
      d,
      s.shift,
      s.post_id || s.postId,
      s.id
    );
  }}
  style={{
    position:'absolute',
    top:-4,
    right:-4,
    background:'#555',
    border:'none',
    color:'#fff',
    borderRadius:'50%',
    width:14,
    height:14,
    fontSize:9,
    cursor:'pointer',
    display:'flex',
    alignItems:'center',
    justifyContent:'center'
  }}
>
  🔒
</button>
)}                                                                                                                                                                                                                                                                                                                                                                
                              {canEdit&&(<button onClick={e=>{e.stopPropagation();openNoPuedeCubrir(s);}} style={{position:'absolute',bottom:-4,right:-4,background:'#fbbf24',border:'none',color:'#000',borderRadius:'50%',width:14,height:14,fontSize:9,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>!</button>)}
                            </div>
                          ))}
                        </div>
                      ):(
                        <div style={{color:'#2a2a2a',cursor:'pointer',fontSize:18,lineHeight:'32px',borderRadius:4,border:'1px dashed #222'}} onClick={()=>openAssign(emp.id,d)} onDragOver={e=>e.preventDefault()} onDrop={()=>onDrop(emp.id,d)}>{canEdit?'+':'🔒'}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal&&!conflictInfo&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div style={{background:'#1a1a1a',border:'1px solid #333',borderRadius:16,padding:'2rem',width:'100%',maxWidth:420}}>
            <h3 style={{color:'#F5C518',margin:'0 0 1rem',fontSize:16}}>Asignar turno - {employees.find(e=>e.id===modal.empId)?.name}</h3>
            <p style={{color:'#888',fontSize:13,margin:'0 0 1rem'}}>{DIAS_FULL[modal.date.getDay()]}, {fmt(modal.date)}</p>
            {getEmpAppointment(modal.empId, modal.date) && (
              <div style={{background:'rgba(96,165,250,0.12)',border:'1px solid rgba(96,165,250,0.3)',borderRadius:6,padding:'8px 12px',marginBottom:12,fontSize:12,color:'#93c5fd'}}>
                ⚠️ Este empleado tiene <strong>{getEmpAppointment(modal.empId, modal.date).type}</strong> este día. Asignar de todas formas confirmará un turno sobre ese evento.
              </div>
            )}
            <label style={{display:'block',color:'#888',fontSize:12,marginBottom:4}}>Puesto</label>
            <select value={modal.postId} onChange={e=>{const post=posts.find(p=>String(p.id)===String(e.target.value));setModal({...modal,postId:e.target.value,shift:post?.shifts?.[0]||'08:00/16:00'});}} style={{width:'100%',padding:'10px',background:'#0d0d0d',border:'1px solid #333',borderRadius:8,color:'#fff',fontSize:14,marginBottom:12,boxSizing:'border-box'}}>
              <option value=''>Sin puesto especifico</option>
              {posts.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <label style={{display:'block',color:'#888',fontSize:12,marginBottom:4}}>Turno</label>
            <select value={modal.shift} onChange={e=>setModal({...modal,shift:e.target.value})} style={{width:'100%',padding:'10px',background:'#0d0d0d',border:'1px solid #333',borderRadius:8,color:'#fff',fontSize:14,marginBottom:10,boxSizing:'border-box'}}>
              {(posts.find(p=>String(p.id)===String(modal.postId))?.shifts||['08:00/16:00']).map(s=><option key={s} value={s}>{formatShift12h(s)}</option>)}
            </select>
            <button type='button' onClick={()=>setSpecialShift(v=>!v)} style={{marginBottom:12,padding:'6px 10px',background:'#222',color:'#F5C518',border:'1px solid #444',borderRadius:6,cursor:'pointer',fontSize:12}}>{specialShift?'Usar turno predeterminado':'Horario especial'}</button>
            {specialShift&&(<><label style={{display:'block',color:'#888',fontSize:12,marginBottom:4}}>Entrada</label><div style={{display:'flex',gap:6,marginBottom:10}}><input type='number' min='1' max='12' value={modal.startHour||''} onChange={e=>setModal({...modal,startHour:e.target.value})} style={{width:'60px',padding:'8px'}}/><select value={modal.startMin||'00'} onChange={e=>setModal({...modal,startMin:e.target.value})}><option value='00'>00</option><option value='15'>15</option><option value='30'>30</option><option value='45'>45</option></select><select value={modal.startAmpm||'AM'} onChange={e=>setModal({...modal,startAmpm:e.target.value})}><option>AM</option><option>PM</option></select></div><label style={{display:'block',color:'#888',fontSize:12,marginBottom:4}}>Salida</label><div style={{display:'flex',gap:6,marginBottom:16}}><input type='number' min='1' max='12' value={modal.endHour||''} onChange={e=>setModal({...modal,endHour:e.target.value})} style={{width:'60px',padding:'8px'}}/><select value={modal.endMin||'00'} onChange={e=>setModal({...modal,endMin:e.target.value})}><option value='00'>00</option><option value='15'>15</option><option value='30'>30</option><option value='45'>45</option></select><select value={modal.endAmpm||'PM'} onChange={e=>setModal({...modal,endAmpm:e.target.value})}><option>AM</option><option>PM</option></select></div></>)}
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>{if(specialShift&&(!modal.startHour||!modal.endHour)){alert('Completa entrada y salida.');return;}const shift=specialShift?to24h(modal.startHour,modal.startMin,modal.startAmpm)+'/'+to24h(modal.endHour,modal.endMin,modal.endAmpm):modal.shift;assignShift(modal.empId,modal.date,shift,modal.postId);}} style={{flex:1,padding:'10px',background:'#F5C518',color:'#000',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer'}}>Asignar</button>
              <button onClick={()=>setModal(null)} style={{flex:1,padding:'10px',background:'rgba(220,38,38,0.15)',color:'#f87171',border:'1px solid rgba(220,38,38,0.3)',borderRadius:8,cursor:'pointer',fontWeight:600}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {conflictInfo&&pendingAssign&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'#1a1a1a',border:'1px solid '+(conflictInfo.type==='block'?'rgba(239,68,68,0.5)':'rgba(251,191,36,0.5)'),borderRadius:16,padding:'2rem',width:'100%',maxWidth:420}}>
            <div style={{fontSize:32,textAlign:'center',marginBottom:12}}>{conflictInfo.type==='block'?'🚫':'⚠️'}</div>
            <h3 style={{color:conflictInfo.type==='block'?'#f87171':'#fbbf24',margin:'0 0 1rem',textAlign:'center'}}>{conflictInfo.type==='block'?'Restriccion':'Atencion'}</h3>
            <p style={{color:'#ccc',fontSize:14,textAlign:'center',marginBottom:'1.5rem'}}>{conflictInfo.msg}</p>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>{assignShift(pendingAssign.empId,pendingAssign.date,pendingAssign.shift,pendingAssign.postId,true);setModal(null);}} style={{flex:1,padding:'10px',background:conflictInfo.type==='block'?'rgba(239,68,68,0.2)':'#F5C518',color:conflictInfo.type==='block'?'#f87171':'#000',border:conflictInfo.type==='block'?'1px solid rgba(239,68,68,0.4)':'none',borderRadius:8,cursor:'pointer',fontWeight:700}}>{conflictInfo.type==='block'?'Override':'Si, asignar'}</button>
              <button onClick={()=>{setConflictInfo(null);setPendingAssign(null);}} style={{flex:1,padding:'10px',background:'#333',color:'#fff',border:'1px solid #444',borderRadius:8,cursor:'pointer',fontWeight:600}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {noPuedeCubrir&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#1a1a1a',padding:20,borderRadius:12,width:400}}>
            <h3 style={{color:'#F5C518'}}>Reemplazo de turno</h3>
            <p style={{color:'#f87171'}}>{formatShift12h(noPuedeCubrir.schedule.shift)} en {noPuedeCubrir.vacancy.post.name}</p>
            <div style={{marginTop:10}}><div style={{color:'#F5C518',fontSize:12,fontWeight:700}}>Sugerencias:</div>
            {(()=>{const opts=getReplacementOptions(noPuedeCubrir.vacancy);return(<>
              {opts.available.slice(0,5).map(emp=>(<div key={emp.id} onClick={async()=>{await assignShift(emp.id,parseDateStr(noPuedeCubrir.vacancy.date),noPuedeCubrir.vacancy.shift,noPuedeCubrir.vacancy.post.id);await deleteSchedule(noPuedeCubrir.schedule.id);setNoPuedeCubrir(null);await reload();}} style={{color:'#34d399',fontSize:12,marginTop:6,cursor:'pointer'}}>{emp.name}</div>))}
              {opts.limited.length>0&&(<div style={{marginTop:12}}><div style={{color:'#fbbf24',fontSize:12,fontWeight:700}}>Con conflicto:</div>{opts.limited.slice(0,5).map(({emp,reason})=>(<div key={emp.id} onClick={async()=>{if(!window.confirm(emp.name+' conflicto: '+reason+'. Continuar?'))return;await assignShift(emp.id,parseDateStr(noPuedeCubrir.vacancy.date),noPuedeCubrir.vacancy.shift,noPuedeCubrir.vacancy.post.id,true);await deleteSchedule(noPuedeCubrir.schedule.id);setNoPuedeCubrir(null);await reload();}} style={{color:'#f87171',fontSize:11,marginTop:5,cursor:'pointer'}}>{emp.name} - {reason}</div>))}</div>)}
            </>);})()}
            </div>
            <div style={{display:'flex',gap:10,marginTop:20}}>
              <button onClick={async()=>{await deleteSchedule(noPuedeCubrir.schedule.id);setNoPuedeCubrir(null);await reload();}} style={{flex:1,background:'#f87171',padding:10,borderRadius:6}}>Liberar</button>
              <button onClick={()=>setNoPuedeCubrir(null)} style={{flex:1,background:'#444',padding:10,borderRadius:6}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
